<template>
  <div class="child-payments-container">
    <div class="payments-header">
      <div class="header-info">
        <h2>Fizetések Kezelése</h2>
        <div class="loan-counter" v-if="loanCount > 0">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 6v6l4 2"/>
          </svg>
          <span>Részletfizetések: {{ loanCount }}</span>
        </div>
      </div>
      <button @click="showAddPaymentmdl = true" class="btn-primary">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Új Fizetés
      </button>
    </div>

    <!-- Loan Alert -->
    <div v-if="loanCount > 0" class="loan-alert">
      <div class="alert-content">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <span>Figyelem! A tanítvány {{ loanCount }} részletfizetéssel rendelkezik.</span>
      </div>
    </div>

    <!-- Payments List -->
    <div class="payments-list">
      <!-- Show only last payment by default -->
      <div v-if="!showAllPayments && payments?.length > 0" class="payment-card">
        <div class="payment-header">
          <div class="payment-info">
            <h3>Jegy #{{ getPaymentNumber(payments[0].id) }}</h3>
            <span class="payment-date">{{ formatDate(payments[0].payment_date) }}</span>
          </div>
          <div class="payment-status">
            <span :class="'status-' + payments[0].status">{{ getStatusText(payments[0].status) }}</span>
          </div>
        </div>
        
        <div class="payment-details">
          <div class="amounts-grid">
            <div class="amount-item paid">
              <span class="label">Fizetve</span>
              <span class="amount">{{ formatPrice(payments[0].amount_paid) }}</span>
            </div>
            <div class="amount-item due">
              <span class="label">Hátralék</span>
              <span class="amount">{{ formatPrice(payments[0].amount_due) }}</span>
            </div>
          </div>
          
          <div class="payment-meta">
            <p><strong>Fizetési mód:</strong> {{ payments[0].payment_method }}</p>
            <p v-if="payments[0].notes"><strong>Megjegyzés:</strong> {{ payments[0].notes }}</p>
          </div>
          
          <div class="payment-actions">
            <button
              @click="addAdditionalPayment(payments[0])"
              class="btn-secondary"
              v-if="payments[0].amount_due > 0"
            >
              További fizetés
            </button>
          </div>
        </div>
      </div>

      <!-- Show all payments when expanded -->
      <div v-if="showAllPayments">
        <div v-for="(payment, index) in payments" :key="payment.id" class="payment-card">
          <div class="payment-header">
            <div class="payment-info">
              <h3>Jegy #{{ getPaymentNumber(payment.id) }}</h3>
              <span class="payment-date">{{ formatDate(payment.payment_date) }}</span>
            </div>
            <div class="payment-status">
              <span :class="'status-' + payment.status">{{ getStatusText(payment.status) }}</span>
            </div>
          </div>
        
        <div class="payment-details">
          <div class="amounts-grid">
            <div class="amount-item paid">
              <span class="label">Fizetve</span>
              <span class="amount">{{ formatPrice(payment.amount_paid) }}</span>
            </div>
            <div class="amount-item due">
              <span class="label">Hátralék</span>
              <span class="amount">{{ formatPrice(payment.amount_due) }}</span>
            </div>
          </div>
          
          <div class="payment-meta">
            <p><strong>Fizetési mód:</strong> {{ payment.payment_method }}</p>
            <p v-if="payment.notes"><strong>Megjegyzés:</strong> {{ payment.notes }}</p>
          </div>
          
          <div class="payment-actions">
            <button
              @click="addAdditionalPayment(payment)"
              class="btn-secondary"
              v-if="payment.amount_due > 0"
            >
              További fizetés
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Dropdown button to show/hide all payments -->
    <div v-if="payments?.length > 1" class="dropdown-container">
      <button @click="toggleShowAllPayments" class="btn-dropdown">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M6 9l6 6 6-6"/>
        </svg>
        {{ showAllPayments ? 'Összes fizetés elrejtése' : `Összes fizetés megjelenítése (${payments?.length})` }}
      </button>
    </div>

    <!-- No payments message -->
    <div v-if="payments?.length === 0" class="no-payments">
      <div class="no-payments-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      </div>
      <h3>Nincsenek fizetések</h3>
      <p>Még nincsenek rögzítve fizetések ehhez a gyerekhez.</p>
      <button @click="showAddPaymentmdl = true" class="btn-primary">
        Első fizetés hozzáadása
      </button>
    </div>

    <!-- Add Payment mdl -->
    <div v-if="showAddPaymentmdl" class="mdl-overlay" @click="closeAddPaymentmdl">
      <div class="mdl-content" @click.stop>
        <div class="mdl-header">
          <h3>Új Fizetés Hozzáadása</h3>
          <button @click="closeAddPaymentmdl" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="mdl-body">
          <form @submit.prevent="savePayment">
            <div class="form-group">
              <label for="ticketSelect">Jegy típusa:</label>
              <select id="ticketSelect" v-model="paymentForm.ticket_id" required>
                <option value="">Válassz jegyet...</option>
                <option v-for="ticket in availableTickets" :key="ticket.id" :value="ticket.id">
                  {{ ticket.name }} - {{ formatPrice(ticket.price) }}
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="amountPaid">Fizetett összeg (Ft):</label>
              <input 
                id="amountPaid" 
                v-model.number="paymentForm.amount_paid" 
                type="number" 
                step="0.01" 
                min="0"
                :max="getSelectedTicketPrice"
                @input="validateAmount"
                required 
                placeholder="0.00"
              />
            </div>
            
            <div class="form-group">
              <label for="paymentMethod">Fizetési mód:</label>
              <select id="paymentMethod" v-model="paymentForm.payment_method" required>
                <option value="cash">Készpénz</option>
                <option value="card">Bankkártya</option>
                <option value="transfer">Átutalás</option>
                <option value="other">Egyéb</option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="paymentNotes">Megjegyzés:</label>
              <textarea 
                id="paymentNotes" 
                v-model="paymentForm.notes" 
                placeholder="Fizetésről szóló megjegyzés..."
              ></textarea>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="closeAddPaymentmdl" class="btn-secondary">Mégse</button>
              <button type="submit" class="btn-primary" :disabled="saving">
                {{ saving ? 'Mentés...' : 'Fizetés hozzáadása' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Additional Payment mdl -->
    <div v-if="showAdditionalPaymentmdl" class="mdl-overlay" @click="closeAdditionalPaymentmdl">
      <div class="mdl-content" @click.stop>
        <div class="mdl-header">
          <h3>További Fizetés</h3>
          <button @click="closeAdditionalPaymentmdl" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="mdl-body">
          <form @submit.prevent="saveAdditionalPayment">
            <div class="payment-summary">
              <h4>Jelenlegi fizetés</h4>
              <p><strong>Fizetve:</strong> {{ formatPrice(selectedPayment.amount_paid) }}</p>
              <p><strong>Hátralék:</strong> {{ formatPrice(selectedPayment.amount_due) }}</p>
            </div>
            
            <div class="form-group">
              <label for="additionalAmount">További fizetett összeg (Ft):</label>
              <input 
                id="additionalAmount" 
                v-model.number="additionalPaymentForm.amount_paid" 
                type="number" 
                step="0.01" 
                required 
                :max="selectedPayment.amount_due"
                @input="validateAmount"

                placeholder="0.00"
              />
            </div>
            
            <div class="form-group">
              <label for="additionalPaymentMethod">Fizetési mód:</label>
              <select id="additionalPaymentMethod" v-model="additionalPaymentForm.payment_method" required>
                <option value="cash">Készpénz</option>
                <option value="card">Bankkártya</option>
                <option value="transfer">Átutalás</option>
                <option value="other">Egyéb</option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="additionalNotes">Megjegyzés:</label>
              <textarea 
                id="additionalNotes" 
                v-model="additionalPaymentForm.notes" 
                placeholder="További fizetésről szóló megjegyzés..."
              ></textarea>
            </div>
            
            <div class="form-actions">
              <button type="button" @click="closeAdditionalPaymentmdl" class="btn-secondary">Mégse</button>
              <button type="submit" class="btn-primary" :disabled="saving">
                {{ saving ? 'Mentés...' : 'Fizetés hozzáadása' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    childId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      payments: [],
      availableTickets: [],
      loanCount: 0,
      showAddPaymentmdl: false,
      showAdditionalPaymentmdl: false,
      showAllPayments: false,
      saving: false,
      selectedPayment: null,
      paymentForm: {
        child_id: parseInt(this.childId),
        ticket_id: '',
        amount_paid: 0,
        payment_method: 'cash',
        notes: ''
      },
      additionalPaymentForm: {
        amount_paid: 0,
        payment_method: 'cash',
        notes: ''
      }
    };
  },
  created() {
    this.loadPayments();
    this.loadAvailableTickets();
    this.loadLoanCount();
  },
  methods: {
    validateAmount(event) {
    const value = parseFloat(event.target.value);
    const max = this.getSelectedTicketPrice;
    
    if (isNaN(value)) {
      this.paymentForm.amount_paid = 0;
    } else if (value < 0) {
      this.paymentForm.amount_paid = 0;
    } else if (value > max) {
      this.paymentForm.amount_paid = max;
    } else {
      this.paymentForm.amount_paid = value;
    }
  },
    async loadPayments() {
      try {
        const response = await axios.get(this.$BACKEND_HOST + `/api/pricing/children/${this.childId}/payments`);
        this.payments = response.data.payments;
      } catch (error) {
        console.error('Error loading payments:', error);
      }
    },
    async loadAvailableTickets() {
      try {
        const response = await axios.get(this.$BACKEND_HOST + '/api/pricing/tickets/active');
        this.availableTickets = response.data.tickets;
      } catch (error) {
        console.error('Error loading tickets:', error);
      }
    },
    async loadLoanCount() {
      try {
        // Get child data which includes loan count
        const response = await axios.get(this.$BACKEND_HOST + `/api/child/${this.childId}`);
        console.log(response.data)
        this.loanCount = response.data.data.loan_count.Int64 || 0;
      } catch (error) {
        console.error('Error loading loan count:', error);
        this.loanCount = 0;
      }
    },
    async savePayment() {
      this.saving = true;
      try {
        this.paymentForm.child_id = parseInt(this.childId);
        
        // Save the payment
        await axios.post(this.$BACKEND_HOST + '/api/pricing/children/payments', this.paymentForm);
        
        this.closeAddPaymentmdl();
        await Promise.all([this.loadPayments(), this.loadLoanCount()]);
      } catch (error) {
        console.error('Error saving payment:', error);
      }
      this.saving = false;
    },
    async saveAdditionalPayment() {
      this.saving = true;
      try {
        this.additionalPaymentForm.child_id = parseInt(this.childId);
        await axios.put(this.$BACKEND_HOST + `/api/pricing/payments/${this.selectedPayment.id}`, this.additionalPaymentForm);
        this.closeAdditionalPaymentmdl();
        await Promise.all([this.loadPayments(), this.loadLoanCount()]);
      } catch (error) {
        console.error('Error saving additional payment:', error);
      }
      this.saving = false;
    },
    addAdditionalPayment(payment) {
      this.selectedPayment = payment;
      this.additionalPaymentForm = {
        amount_paid: 0,
        payment_method: 'cash',
        notes: ''
      };
      this.showAdditionalPaymentmdl = true;
    },
    closeAddPaymentmdl() {
      this.showAddPaymentmdl = false;
      this.paymentForm = {
        child_id: this.childId,
        ticket_id: '',
        amount_paid: 0,
        payment_method: 'cash',
        notes: ''
      };
    },
    closeAdditionalPaymentmdl() {
      this.showAdditionalPaymentmdl = false;
      this.selectedPayment = null;
      this.additionalPaymentForm = {
        amount_paid: 0,
        payment_method: 'cash',
        notes: ''
      };
    },
    formatPrice(price) {
      return new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: 'HUF'
      }).format(price);
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('hu-HU');
    },
    getStatusText(status) {
      const statusMap = {
        'completed': 'Teljesítve',
        'partial': 'Részleges',
        'pending': 'Függőben'
      };
      return statusMap[status] || status;
    },
    toggleShowAllPayments() {
      this.showAllPayments = !this.showAllPayments;
    },
    getPaymentNumber(paymentId) {
      // Find the index of this payment in the sorted array (newest first)
      const index = this.payments?.findIndex(p => p.id === paymentId);
      return index + 1; // Return 1-based index
    },
    getChildName() {
      // This would need to be implemented based on how child name is retrieved in this component
      // For now, return a placeholder - you may need to pass child name as prop or fetch it
      return `Child ${this.childId}`;
    }
  },
  computed: {
   getSelectedTicketPrice() {
      const ticket = this.availableTickets.find(
        (t) => t.id === this.paymentForm.ticket_id
      );
      return ticket ? ticket.price : null;
    }
  }
};
</script>

<style scoped>
.child-payments-container {
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
}

.payments-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--color-border);
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.payments-header h2 {
  color: var(--color-heading);
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0;
}

.loan-counter {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 20px;
  color: var(--warning);
  font-size: 0.875rem;
  font-weight: 600;
  width: fit-content;
}

.loan-alert {
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(245, 158, 11, 0.05);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 12px;
}

.alert-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--warning);
  font-weight: 500;
}

.payments-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.payment-card {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  overflow: hidden;
  transition: all 0.3s ease;
}

.payment-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.payment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.payment-info h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
}

.payment-date {
  color: var(--color-text-muted);
  font-size: 0.9rem;
}

.payment-status {
  text-align: right;
}

.status-completed {
  color: var(--success);
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  background: rgba(34, 197, 94, 0.1);
  border-radius: 20px;
  font-size: 0.875rem;
}

.status-partial {
  color: var(--warning);
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  background: rgba(245, 158, 11, 0.1);
  border-radius: 20px;
  font-size: 0.875rem;
}

.status-pending {
  color: var(--error);
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 20px;
  font-size: 0.875rem;
}

.payment-details {
  padding: 1.5rem;
}

.amounts-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.amount-item {
  text-align: center;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid var(--color-border);
}

.amount-item.paid {
  background: rgba(34, 197, 94, 0.05);
  border-color: rgba(34, 197, 94, 0.2);
}

.amount-item.due {
  background: rgba(239, 68, 68, 0.05);
  border-color: rgba(239, 68, 68, 0.2);
}

.amount-item .label {
  display: block;
  font-size: 0.875rem;
  color: var(--color-text-muted);
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.amount-item .amount {
  font-size: 1.5rem;
  font-weight: 700;
}

.amount-item.paid .amount {
  color: var(--success);
}

.amount-item.due .amount {
  color: var(--error);
}

.payment-meta {
  margin-bottom: 1.5rem;
}

.payment-meta p {
  margin: 0.5rem 0;
  color: var(--color-text);
}

.payment-actions {
  text-align: right;
}

.no-payments {
  text-align: center;
  padding: 4rem 2rem;
  color: var(--color-text-muted);
}

.no-payments-icon {
  margin-bottom: 1.5rem;
  opacity: 0.5;
}

.no-payments h3 {
  color: var(--color-heading);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.no-payments p {
  margin-bottom: 2rem;
  font-size: 1.1rem;
}

/* mdl Styles */
.mdl-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.mdl-content {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow-xl);
  border: 1px solid var(--color-border);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.mdl-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.mdl-header h3 {
  color: var(--color-heading);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 50%;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: var(--error);
  color: white;
  border-color: var(--error);
}

.mdl-body {
  padding: 2rem;
}

.payment-summary {
  background: var(--color-background-mute);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border: 1px solid var(--color-border);
}

.payment-summary h4 {
  color: var(--color-heading);
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
}

.payment-summary p {
  margin: 0.25rem 0;
  color: var(--color-text);
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--color-heading);
  font-weight: 500;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text);
  font-size: 1rem;
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
}

.btn-primary, .btn-secondary {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: var(--primary-blue);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--secondary-blue);
  transform: translateY(-1px);
}

.btn-secondary {
  background: var(--color-background-mute);
  color: var(--color-text);
  border: 1px solid var(--color-border);
}

.btn-secondary:hover {
  background: var(--color-border);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Dropdown button styles */
.dropdown-container {
  text-align: center;
  margin: 1.5rem 0;
}

.btn-dropdown {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--color-background-mute);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-dropdown:hover {
  background: var(--primary-blue);
  color: white;
  border-color: var(--primary-blue);
  transform: translateY(-1px);
}

.btn-dropdown svg {
  transition: transform 0.3s ease;
}

.btn-dropdown:hover svg {
  transform: rotate(180deg);
}

/* Responsive Design */
@media (max-width: 768px) {
  .child-payments-container {
    padding: 1rem;
  }
  
  .payments-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .header-info {
    align-items: center;
    text-align: center;
  }
  
  .amounts-grid {
    grid-template-columns: 1fr;
  }
  
  .mdl-content {
    width: 95%;
    margin: 1rem;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .loan-counter {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
  
  .alert-content {
    font-size: 0.9rem;
  }
}
</style>
